import { DateTime } from 'luxon';

import { MailSequenceReferenceDateType } from '../../../generated/graphql';
import {
  getPracticeTimezoneForScheduling,
  getTimezoneAbbreviation,
} from '../../lib/time';
import { normalizeTimeString, TemplateStepDraft } from '../../lib/mailSequence';
import {
  anchorDateFieldForReferenceType,
  formatProgramDate,
  isProgramDateSet,
  patientDisplayName,
  toDateInputValue,
} from './anchorDates';
import type { PatientNameDataFragment } from '../../../generated/graphql';
import {
  MailAlertSegment,
  mailSegment,
  segmentsToPlainText,
  textSegment,
} from '../../components/MailAlertMessage';

/** Matches server ``MAX_SEQUENCE_ENROLL_LOOKBACK_DAYS`` (enrollment_service.py). */
export const ENROLL_SKIP_LOOKBACK_DAYS = 14;

export type MailTimingIssueSeverity = 'error' | 'warning';

export type MailTimingIssue = {
  stepIndex: number;
  severity: MailTimingIssueSeverity;
  /** Bullet body; may include Mail step badges. */
  segments: MailAlertSegment[];
  programInstanceId?: string;
};

type ValidateMailTimingInput = {
  recipients: PatientNameDataFragment[];
  referenceDateType: MailSequenceReferenceDateType;
  customAnchorDate: string;
  steps: TemplateStepDraft[];
  defaultSendTime: string;
  providerTimezone?: string | null;
};

const scheduleToday = (providerTimezone?: string | null): DateTime =>
  DateTime.now()
    .setZone(getPracticeTimezoneForScheduling(providerTimezone))
    .startOf('day');

const scheduleNow = (providerTimezone?: string | null): DateTime =>
  DateTime.now().setZone(getPracticeTimezoneForScheduling(providerTimezone));

const buildSendDateTime = (
  scheduledDay: DateTime,
  defaultSendTime: string,
  providerTimezone?: string | null,
): DateTime | null => {
  const tz = getPracticeTimezoneForScheduling(providerTimezone);
  const normalized = normalizeTimeString(defaultSendTime);
  const [hh, mm] = normalized.split(':');
  const hour = Number(hh);
  const minute = Number(mm);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;

  const dayInTz = scheduledDay
    .setZone(tz, { keepLocalTime: true })
    .startOf('day');
  return dayInTz.set({ hour, minute, second: 0, millisecond: 0 });
};

const formatSendTimeLabel = (
  defaultSendTime: string,
  providerTimezone?: string | null,
): string => {
  const normalized = normalizeTimeString(defaultSendTime);
  const tz = getPracticeTimezoneForScheduling(providerTimezone);
  const abbr = getTimezoneAbbreviation(tz);
  const dt = buildSendDateTime(
    DateTime.now().setZone(tz).startOf('day'),
    normalized,
    providerTimezone,
  );
  const timeLabel = dt?.isValid
    ? dt.toLocaleString(DateTime.TIME_SIMPLE)
    : normalized;
  return `${timeLabel} ${abbr}`.trim();
};

const formatPassedSendTimeSegments = (
  stepIndex: number,
  defaultSendTime: string,
  providerTimezone: string | null | undefined,
): MailAlertSegment[] => [
  mailSegment(stepIndex),
  textSegment(
    ` is scheduled for today at ${formatSendTimeLabel(
      defaultSendTime,
      providerTimezone,
    )}, but that send time has already passed. It will send as soon as you enroll — adjust the send time or pick a future date if that is not intended.`,
  ),
];

const parseScheduleDate = (iso: string): DateTime | null => {
  const dt = DateTime.fromISO(iso.slice(0, 10));
  return dt.isValid ? dt.startOf('day') : null;
};

const resolveAnchorDateForRecipient = (
  recipient: PatientNameDataFragment,
  referenceDateType: MailSequenceReferenceDateType,
  customAnchorDate: string,
): string | null => {
  if (referenceDateType === MailSequenceReferenceDateType.Custom) {
    return customAnchorDate || null;
  }
  const field = anchorDateFieldForReferenceType(referenceDateType);
  if (!field) return null;
  const raw = recipient[field];
  return isProgramDateSet(raw) ? toDateInputValue(raw) : null;
};

/** User-facing date type label (never "anchor"). */
export const scheduleDateTypeLabel = (
  referenceDateType: MailSequenceReferenceDateType,
  customDateIso?: string | null,
): string => {
  if (referenceDateType === MailSequenceReferenceDateType.ProgramStartDate) {
    return 'program start date';
  }
  if (referenceDateType === MailSequenceReferenceDateType.ProgramEndDate) {
    return 'program end date';
  }
  if (customDateIso?.trim()) {
    return 'selected date';
  }
  return 'selected date';
};

const formatMissingDateSegments = (
  referenceDateType: MailSequenceReferenceDateType,
): MailAlertSegment[] => {
  if (referenceDateType === MailSequenceReferenceDateType.Custom) {
    return [textSegment('Pick a date above before scheduling Mail.')];
  }
  const label = scheduleDateTypeLabel(referenceDateType);
  return [
    textSegment(
      `The ${label} is not set. Set it on each client's Account tab before scheduling.`,
    ),
  ];
};

const formatPastScheduledSegments = (
  stepIndex: number,
  scheduled: DateTime,
  today: DateTime,
  referenceDateType: MailSequenceReferenceDateType,
  customAnchorDate: string,
): MailAlertSegment[] => {
  const formattedDate =
    formatProgramDate(scheduled.toISODate()) ?? scheduled.toISODate();
  const lookbackCutoff = today.minus({ days: ENROLL_SKIP_LOOKBACK_DAYS });
  const dateTypeLabel = scheduleDateTypeLabel(
    referenceDateType,
    customAnchorDate,
  );

  if (scheduled < lookbackCutoff) {
    return [
      mailSegment(stepIndex),
      textSegment(
        ` is scheduled for ${formattedDate}, which is more than ${ENROLL_SKIP_LOOKBACK_DAYS} days in the past and will be skipped when you enroll.`,
      ),
    ];
  }

  return [
    mailSegment(stepIndex),
    textSegment(
      ` is scheduled for ${formattedDate}, which is already in the past. Adjust the timing or the ${dateTypeLabel} before enrolling.`,
    ),
  ];
};

const formatOutOfOrderSegments = (
  earlierStepIndex: number,
  laterStepIndex: number,
): MailAlertSegment[] => [
  mailSegment(laterStepIndex),
  textSegment(' is scheduled before '),
  mailSegment(earlierStepIndex),
  textSegment('. Mails should go out in chronological order.'),
];

export const validateMailTiming = ({
  recipients,
  referenceDateType,
  customAnchorDate,
  steps,
  defaultSendTime,
  providerTimezone,
}: ValidateMailTimingInput): MailTimingIssue[] => {
  if (recipients.length === 0 || steps.length === 0) return [];

  if (
    referenceDateType === MailSequenceReferenceDateType.Custom &&
    !customAnchorDate.trim()
  ) {
    return [
      {
        stepIndex: 0,
        severity: 'error',
        segments: formatMissingDateSegments(referenceDateType),
      },
    ];
  }

  const today = scheduleToday(providerTimezone);
  const now = scheduleNow(providerTimezone);
  const issues: MailTimingIssue[] = [];

  type RecipientSchedule = {
    programInstanceId: string;
    name: string;
    scheduledByStep: Array<DateTime | null>;
  };

  const recipientSchedules: RecipientSchedule[] = [];
  const missingAnchorRecipients: Array<{
    programInstanceId: string;
    name: string;
  }> = [];

  recipients.forEach((recipient) => {
    const anchorIso = resolveAnchorDateForRecipient(
      recipient,
      referenceDateType,
      customAnchorDate,
    );
    const name = patientDisplayName(recipient);

    if (!anchorIso) {
      missingAnchorRecipients.push({
        programInstanceId: recipient.programInstanceId,
        name,
      });
      recipientSchedules.push({
        programInstanceId: recipient.programInstanceId,
        name,
        scheduledByStep: steps.map(() => null),
      });
      return;
    }

    const scheduledByStep = steps.map((step) => {
      const anchor = parseScheduleDate(anchorIso);
      if (!anchor) return null;
      return anchor.plus({ days: step.offsetDays });
    });

    recipientSchedules.push({
      programInstanceId: recipient.programInstanceId,
      name,
      scheduledByStep,
    });
  });

  if (missingAnchorRecipients.length > 0) {
    issues.push({
      stepIndex: 0,
      severity: 'error',
      segments: formatMissingDateSegments(referenceDateType),
      programInstanceId:
        missingAnchorRecipients.length === 1
          ? missingAnchorRecipients[0].programInstanceId
          : undefined,
    });
  }

  steps.forEach((_, stepIndex) => {
    const pastRecipients = recipientSchedules.filter(({ scheduledByStep }) => {
      const scheduled = scheduledByStep[stepIndex];
      return scheduled != null && scheduled < today;
    });

    if (pastRecipients.length > 0) {
      const sample = pastRecipients[0];
      const scheduled = sample.scheduledByStep[stepIndex]!;
      issues.push({
        stepIndex,
        severity: 'error',
        segments: formatPastScheduledSegments(
          stepIndex,
          scheduled,
          today,
          referenceDateType,
          customAnchorDate,
        ),
        programInstanceId:
          pastRecipients.length === 1 ? sample.programInstanceId : undefined,
      });
    }
  });

  steps.forEach((_, stepIndex) => {
    const passedSendTimeRecipients = recipientSchedules.filter(
      ({ scheduledByStep }) => {
        const scheduled = scheduledByStep[stepIndex];
        if (scheduled == null || !scheduled.hasSame(today, 'day')) return false;
        const sendAt = buildSendDateTime(
          scheduled,
          defaultSendTime,
          providerTimezone,
        );
        return sendAt != null && now >= sendAt;
      },
    );

    if (passedSendTimeRecipients.length > 0) {
      issues.push({
        stepIndex,
        severity: 'warning',
        segments: formatPassedSendTimeSegments(
          stepIndex,
          defaultSendTime,
          providerTimezone,
        ),
        programInstanceId:
          passedSendTimeRecipients.length === 1
            ? passedSendTimeRecipients[0].programInstanceId
            : undefined,
      });
    }
  });

  if (steps.length > 1) {
    for (let stepIndex = 1; stepIndex < steps.length; stepIndex += 1) {
      const outOfOrderRecipients = recipientSchedules.filter(
        ({ scheduledByStep }) => {
          const earlier = scheduledByStep[stepIndex - 1];
          const later = scheduledByStep[stepIndex];
          return earlier != null && later != null && later < earlier;
        },
      );

      if (outOfOrderRecipients.length > 0) {
        issues.push({
          stepIndex,
          severity: 'error',
          segments: formatOutOfOrderSegments(stepIndex - 1, stepIndex),
          programInstanceId:
            outOfOrderRecipients.length === 1
              ? outOfOrderRecipients[0].programInstanceId
              : undefined,
        });
      }
    }
  }

  return issues;
};

export const getMailTimingIssuesForStep = (
  issues: MailTimingIssue[],
  stepIndex: number,
): MailTimingIssue[] => issues.filter((issue) => issue.stepIndex === stepIndex);

export const hasMailTimingErrors = (issues: MailTimingIssue[]): boolean =>
  issues.some((issue) => issue.severity === 'error');

export const getMailTimingErrorMessages = (
  issues: MailTimingIssue[],
): string[] =>
  issues
    .filter((issue) => issue.severity === 'error')
    .map((issue) => segmentsToPlainText(issue.segments));
